import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { motion } from 'framer-motion';
import { ArrowLeft, Bot, Users, TrendingDown, Settings, PauseCircle, Archive as ArchiveIcon, PlayCircle, ListTree } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  active: '#40916C',
  retired: '#E9C46A',
  archived: '#6B7280',
};

const PRIORITY_LABELS = {
  friends: 'Friends',
  live_humans: 'Live Humans (15m)',
  recent_humans: 'Recent Humans (7d)',
  similar_skill: 'Similar Skill (±rating)',
  any_human: 'Any Human',
  ai_explorers: 'AI Explorers',
};

function Stat({ label, value, tint }) {
  return (
    <div className="p-3 rounded-2xl glass border border-white/10">
      <p className="text-[10px] uppercase tracking-widest text-white/50">{label}</p>
      <p className="text-2xl font-serif mt-1" style={tint ? { color: tint } : undefined}>{value}</p>
    </div>
  );
}

export default function AIExplorerAdmin() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [explorers, setExplorers] = useState([]);
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState('active'); // active | retired | archived
  const [busy, setBusy] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  const loadAll = async () => {
    try {
      const [s, c, ex, ev] = await Promise.all([
        api.get('/ai-admin/stats'),
        api.get('/ai-admin/config'),
        api.get(`/ai-admin/explorers?status=${tab}&limit=100`),
        api.get('/ai-admin/events?limit=20'),
      ]);
      setStats(s.data); setConfig(c.data);
      setExplorers(ex.data.explorers || []);
      setEvents(ev.data.events || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403) {
        toast.error('Admin access required');
        nav('/dashboard', { replace: true });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to load AI Explorer data');
      }
    }
  };
  useEffect(() => { loadAll(); }, [tab]); // eslint-disable-line

  const doRetireBatch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/ai-admin/retire-batch');
      toast.success(`Retired ${data.retired} AI Explorer(s)`);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Retire batch failed');
    } finally { setBusy(false); }
  };

  const doArchiveStale = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/ai-admin/archive-stale?older_than_days=90');
      toast.success(`Archived ${data.archived} retired AI(s)`);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Archive failed');
    } finally { setBusy(false); }
  };

  const setStatus = async (user_id, new_status) => {
    try {
      await api.post('/ai-admin/set-status', { user_id, new_status, reason: 'admin manual' });
      toast.success(`Status → ${new_status}`);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const saveConfig = async (patch) => {
    setSavingConfig(true);
    try {
      const { data } = await api.patch('/ai-admin/config', patch);
      setConfig(data);
      toast.success('Config updated');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Save failed');
    } finally { setSavingConfig(false); }
  };

  if (!stats || !config) return (
    <div className="min-h-screen bg-[#04120c] flex items-center justify-center text-white/60">Loading…</div>
  );

  const humansPerAI = stats.active_ai_explorers > 0
    ? (stats.active_humans_7d / stats.active_ai_explorers).toFixed(1) : '—';

  return (
    <div className="min-h-screen bg-[#04120c] text-foreground pb-24" data-testid="ai-admin-root">
      <div className="max-w-5xl mx-auto px-4 py-4">
        <button onClick={() => nav(-1)} className="flex items-center gap-1 text-xs text-white/60 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>

        <div className="flex items-center gap-2 mb-4">
          <Bot className="w-5 h-5 text-primary-light" />
          <h1 className="font-serif text-2xl">AI Explorer Management</h1>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="stat-grid">
          <Stat label="Human Players" value={stats.human_players} />
          <Stat label="Active (7d)" value={stats.active_humans_7d} />
          <Stat label="AI Active" value={stats.active_ai_explorers} tint={STATUS_COLORS.active} />
          <Stat label="AI Retired" value={stats.retired_ai_explorers} tint={STATUS_COLORS.retired} />
          <Stat label="AI Archived" value={stats.archived_ai_explorers} tint={STATUS_COLORS.archived} />
          <Stat label="Live Now" value={stats.live_humans_now} />
          <Stat label="H vs H battles" value={`${(stats.ratio_human_vs_human * 100).toFixed(0)}%`} />
          <Stat label="H vs AI battles" value={`${(stats.ratio_human_vs_ai * 100).toFixed(0)}%`} />
        </div>

        {/* Retirement progress bar */}
        <div className="p-4 rounded-3xl glass border border-white/10 mb-4" data-testid="retirement-progress">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-primary-light" />
              <p className="text-sm uppercase tracking-widest">AI Retirement Progress</p>
            </div>
            <span className="text-xs text-white/60">Target active: <span className="text-primary-light">{stats.target_active_ai}</span></span>
          </div>
          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }} animate={{ width: `${stats.ai_retirement_progress_pct}%` }}
              transition={{ duration: 0.8 }}
              className="h-full bg-primary"
            />
          </div>
          <p className="text-[10px] uppercase tracking-widest text-white/50 mt-1">
            {stats.ai_retirement_progress_pct}% · Humans-per-AI ratio: {humansPerAI}
          </p>
          <div className="flex gap-2 mt-3">
            <button
              data-testid="btn-retire-batch"
              onClick={doRetireBatch} disabled={busy}
              className="px-3 py-1.5 rounded-full bg-primary text-white text-xs flex items-center gap-1 disabled:opacity-50"
            >
              <PauseCircle className="w-3 h-3" /> Run Retire Batch
            </button>
            <button
              data-testid="btn-archive-stale"
              onClick={doArchiveStale} disabled={busy}
              className="px-3 py-1.5 rounded-full glass border border-white/10 text-xs flex items-center gap-1"
            >
              <ArchiveIcon className="w-3 h-3" /> Archive Stale (&gt;90d)
            </button>
          </div>
        </div>

        {/* Config editor */}
        <details className="p-4 rounded-3xl glass border border-white/10 mb-4" data-testid="config-editor">
          <summary className="cursor-pointer text-sm uppercase tracking-widest flex items-center gap-2">
            <Settings className="w-4 h-4 text-primary-light" /> Platform Config
          </summary>
          <div className="grid sm:grid-cols-2 gap-3 mt-4 text-xs">
            <label className="space-y-1">
              <span className="text-white/60">Max active AI</span>
              <input
                data-testid="cfg-max-active"
                type="number" defaultValue={config.ai_max_active}
                onBlur={e => saveConfig({ ai_max_active: parseInt(e.target.value, 10) })}
                className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10"
              />
            </label>
            <label className="space-y-1">
              <span className="text-white/60">Retirement ratio (humans/AI target)</span>
              <input
                data-testid="cfg-ratio"
                type="number" step="0.1" defaultValue={config.ai_retirement_ratio_target}
                onBlur={e => saveConfig({ ai_retirement_ratio_target: parseFloat(e.target.value) })}
                className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10"
              />
            </label>
            <label className="space-y-1">
              <span className="text-white/60">Retirement batch size</span>
              <input
                data-testid="cfg-batch"
                type="number" defaultValue={config.ai_retirement_batch_size}
                onBlur={e => saveConfig({ ai_retirement_batch_size: parseInt(e.target.value, 10) })}
                className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10"
              />
            </label>
            <label className="space-y-1">
              <span className="text-white/60">Minimum active humans (7d)</span>
              <input
                data-testid="cfg-min-humans"
                type="number" defaultValue={config.ai_retirement_min_humans_7d}
                onBlur={e => saveConfig({ ai_retirement_min_humans_7d: parseInt(e.target.value, 10) })}
                className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10"
              />
            </label>
          </div>
          <div className="mt-4">
            <p className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Matchmaking Priority (top → bottom)</p>
            <ol className="space-y-1" data-testid="priority-list">
              {config.matchmaking_priority.map((tier, i) => (
                <li key={tier} className="flex items-center gap-2 text-xs">
                  <span className="w-5 text-white/40">{i + 1}.</span>
                  <span className="flex-1">{PRIORITY_LABELS[tier] || tier}</span>
                  <div className="flex gap-1">
                    <button
                      disabled={i === 0 || savingConfig}
                      onClick={() => {
                        const arr = [...config.matchmaking_priority];
                        [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                        saveConfig({ matchmaking_priority: arr });
                      }}
                      className="px-2 py-0.5 rounded-full glass border border-white/10 disabled:opacity-30"
                    >↑</button>
                    <button
                      disabled={i === config.matchmaking_priority.length - 1 || savingConfig}
                      onClick={() => {
                        const arr = [...config.matchmaking_priority];
                        [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
                        saveConfig({ matchmaking_priority: arr });
                      }}
                      className="px-2 py-0.5 rounded-full glass border border-white/10 disabled:opacity-30"
                    >↓</button>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </details>

        {/* Explorer tabs */}
        <div className="flex items-center gap-2 mb-2">
          {['active', 'retired', 'archived'].map(s => (
            <button
              key={s}
              data-testid={`tab-${s}`}
              onClick={() => setTab(s)}
              className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest border ${
                tab === s ? 'bg-primary text-white border-primary' : 'glass border-white/10 text-white/70'
              }`}
            >{s} ({stats[`${s === 'active' ? 'active_ai' : s === 'retired' ? 'retired_ai' : 'archived_ai'}_explorers`]})</button>
          ))}
        </div>

        <ul className="space-y-1 mb-6" data-testid="explorer-list">
          {explorers.map(e => (
            <li key={e.user_id} className="p-2.5 rounded-2xl glass border border-white/10 flex items-center gap-3">
              <img src={e.picture} alt="" className="w-9 h-9 rounded-full border border-white/10" />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{e.nickname || e.name}</p>
                <p className="text-[10px] uppercase tracking-widest text-white/40">
                  {e.email} · XP {e.xp || 0} · {e.ai_status_reason || '—'}
                </p>
              </div>
              <span
                className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest"
                style={{ background: `${STATUS_COLORS[e.ai_status]}22`, color: STATUS_COLORS[e.ai_status] }}
              >{e.ai_status}</span>
              <div className="flex gap-1">
                {e.ai_status !== 'active' && (
                  <button
                    data-testid={`btn-activate-${e.user_id}`}
                    onClick={() => setStatus(e.user_id, 'active')}
                    className="px-2 py-1 rounded-full glass border border-white/10 text-[10px] flex items-center gap-1"
                  ><PlayCircle className="w-3 h-3" /> Activate</button>
                )}
                {e.ai_status !== 'retired' && (
                  <button
                    onClick={() => setStatus(e.user_id, 'retired')}
                    className="px-2 py-1 rounded-full glass border border-white/10 text-[10px]"
                  >Retire</button>
                )}
                {e.ai_status !== 'archived' && (
                  <button
                    onClick={() => setStatus(e.user_id, 'archived')}
                    className="px-2 py-1 rounded-full glass border border-white/10 text-[10px]"
                  >Archive</button>
                )}
              </div>
            </li>
          ))}
          {explorers.length === 0 && (
            <p className="text-center text-white/50 text-sm py-6">No AI explorers in this bucket.</p>
          )}
        </ul>

        {/* Event log */}
        <details className="p-4 rounded-3xl glass border border-white/10" data-testid="event-log">
          <summary className="cursor-pointer text-sm uppercase tracking-widest flex items-center gap-2">
            <ListTree className="w-4 h-4 text-primary-light" /> Lifecycle Event Log
          </summary>
          <ul className="mt-3 space-y-1.5 text-xs">
            {events.map(ev => (
              <li key={ev.created_at + ev.user_id} className="flex items-center gap-2">
                <span className="text-white/40 w-32 truncate">{new Date(ev.created_at).toLocaleString()}</span>
                <span className="text-white/70 truncate flex-1">
                  {ev.user_id.slice(-10)} · {ev.prev_status} → <span className="text-primary-light">{ev.new_status}</span>
                </span>
                <span className="text-white/50 text-[10px] uppercase tracking-widest">{ev.reason}</span>
              </li>
            ))}
            {events.length === 0 && <li className="text-white/50">No events yet.</li>}
          </ul>
        </details>
      </div>
    </div>
  );
}
